/**
 * LA FRAÎCHEUR EN EN-TÊTE (DEV-1508 lot 2, engagement E4).
 * ============================================================================
 *
 * INTENTION : « À jour, dernière mesure le 08/09 » quand tous les flux dus le
 * sont ; sinon la liste des flux en retard, chacun avec sa date et son attendu.
 * Une ligne, pas une bannière : le retard se lit là où on lit le compteur.
 *
 * LA RÈGLE QUI TIENT TOUT : aucun seuil de jours ne vit ici. Le retard, l'attendu
 * et la dernière mesure sont LUS du verdict servi, qui les juge sur l'attendu de
 * CE compteur. Un front qui compare une date à « 2 jours » se trompe sur tous les
 * compteurs dont le flux est mensuel.
 *
 * Le tri-état est respecté partout :
 *  • `streams_en_retard: null` = le juge n'a pas servi le fait. Ce n'est PAS
 *    « aucun retard » : on n'écrit rien plutôt que de rassurer à tort ;
 *  • `streams_en_retard: []` = jugé, et rien en retard : là, et là seulement, on
 *    écrit « À jour » ;
 *  • une date manquante ne s'écrit pas, et ne s'écrit pas non plus « — ».
 */
import type {
    BornesVariableDataV2,
    StatusCollectePdl,
    WorkflowEnedisStreamEnRetard,
    WorkflowEnedisVerdict,
} from "@types"
import type {
    DirectionEnedis,
    GrainSeries,
} from "@modules/data/services/sourceDataEnedis.service"
import type { CleOnglet, OngletResolu } from "@modules/data/utils/ongletsEnedis"
import { LIBELLES_RAPPEL_ONGLET } from "@modules/data/utils/ongletsEnedis"
import { nomClientDuStream } from "@core/composables/workflow-enedis-avancement.helper"
import { jourDeLecture } from "@modules/data/utils/pageCompteurV2"
import {
    acteurEnClair,
    causeDuStatutEnClair,
    collecteInterrompue,
    droitDeCollecteTombe,
} from "@modules/data/utils/libellesEnedis"
import type {
    ContexteDesFlux,
    EtatDeLigne,
} from "@modules/data/utils/fluxEnedis"
import {
    fluxDeLaFamille,
    fraicheurParFlux,
    ligneDeLaFamilleParFlux,
} from "@modules/data/utils/fluxEnedis"

/** Une ligne de fraîcheur à peindre. Rien de plus qu'un texte et une clé. */
export type LigneFraicheur = {
    cle: string
    texte: string
    /**
     * L'ÉTAT DE CETTE LIGNE-LÀ (DEV-1845 tranche 2), quand la fraîcheur est
     * dite flux par flux : sur un compteur dont la courbe est à jour et
     * l'énergie journalière en retard, une pastille unique pour tout le bloc
     * mentirait sur l'une des deux. Absent = la ligne prend l'état du bloc,
     * comme avant.
     */
    etat?: EtatDeLigne
}

export type Fraicheur = {
    /** Vrai seulement quand le juge a dit « aucun flux dû en retard ». */
    aJour: boolean
    lignes: LigneFraicheur[]
    /**
     * L'état de la bande auquel chaque ligne se rattache, quand il est servi.
     * `null` = non servi : le rond reste celui de l'à-jour ou du retard, et
     * aucun responsable n'est nommé.
     */
    etat?: "homeys" | "enedis" | null
    /**
     * LES TROIS FAITS BRUTS DE LA FRAÎCHEUR (lot 5b), pour que le TITRE de la
     * carte puisse dire l'état sans relire une phrase déjà écrite.
     *
     * Ils sont RECOPIÉS de ce qui est servi, jamais dérivés : `retardJours` est
     * le retard jugé sur l'attendu de ce compteur, pas un écart de dates
     * calculé ici (E4).
     */
    derniereMesure?: string | null
    retardJours?: number | null
    /**
     * L'ATTENDU DU FLUX QUI DATE LA DERNIÈRE MESURE, en jours (lot 5h, Q19).
     *
     * IL NE VIENT QUE D'UN ATTENDU SERVI PAR FLUX, et d'aucune autre source.
     * L'agrégat `data_retrieval.status.attendu_jours` vaut 3 sur les trois
     * compteurs témoins, C5, C4 et C2 confondus : c'est un seuil plat de la
     * collecte, pas l'attendu de ce compteur, et le recopier faisait écrire
     * « attendu J+3 » sur un compteur mensuel comme sur un compteur quotidien.
     *
     * `null`/absent = non servi, et c'est le cas AUJOURD'HUI partout : aucune
     * phrase de cette page n'écrit alors d'attendu. Le jour où
     * `attendu_jours_du_flux` sera servi (DEV-1845), les mêmes phrases le
     * reprendront sans autre changement.
     */
    attenduJours?: number | null
    /**
     * FAUX QUAND LE STATUT SE CONTREDIT LUI-MÊME (lot 5h, complément du 17/09).
     *
     * Relevé sur le C2 `679b86d1df35d0c78886fb9d`, sens Soutirage, vu le
     * 17/09 : l'agrégat sert `a_jour: true` avec `derniere_mesure` au
     * 22/08/2026. Le statut est figé au dernier passage de la collecte, et son
     * « à jour » date de ce passage-là, pas d'aujourd'hui.
     *
     * Quand la dernière mesure servie, augmentée de l'attendu SERVI PAR LE MÊME
     * STATUT, tombe avant la veille, le verdict ne peut plus être vrai. On
     * n'écrit alors NI « à jour » NI « en retard de n j » — ce retard, on ne le
     * connaît pas — seulement la date, qui est un fait.
     *
     * Aucun seuil du front n'entre ici : la comparaison ne met en présence que
     * deux champs du même statut et le calendrier (Q4, jamais un état déduit).
     */
    verdictSu?: boolean
    /**
     * Vrai seulement quand il est ÉTABLI qu'aucune mesure n'est jamais arrivée.
     * Une date non servie n'est pas une absence de mesure : c'est une date non
     * servie, et ce drapeau reste faux.
     */
    aucuneMesure?: boolean
    /**
     * LE VERDICT LU DES FLUX DUS (DEV-1845 tranche 2, Q4) : `true` quand ils
     * sont TOUS à jour, `false` dès qu'un seul est en retard, `null` quand le
     * silence des uns empêche de conclure. ABSENT quand `flux` n'est pas servie
     * du tout : c'est alors le verdict agrégé qui parle, comme avant.
     */
    tousAJour?: boolean | null
    /**
     * VRAI QUAND LE DROIT DE COLLECTE EST TOMBÉ (lot 6a, volet 1).
     *
     * Ce n'est pas un retard : rien n'est dû, personne n'est en retard, et
     * aucune phrase de la page n'écrit alors « en retard de n j » ni
     * « attendu J+n ». Absent/faux = le droit tient, et tout se lit comme avant.
     */
    droitPerdu?: boolean
    /**
     * VRAI QUAND LA COLLECTE EST INTERROMPUE (27/10, décision de Mathieu) : le
     * droit tombé en est un cas (`droitPerdu` l'implique), le compteur non
     * téléopérable en est un autre, SANS perte de droit. Tout ce qui tient au
     * FAIT (une seule ligne, ni attendu, ni retard, titre « collecte
     * interrompue ») se lit ici ; ce qui tient au droit perdu reste à
     * `droitPerdu`. Lire par `estInterrompue`, qui accepte l'un ou l'autre.
     */
    collecteInterrompue?: boolean
}

/**
 * La collecte de ce compteur est-elle interrompue ? `droitPerdu` seul suffit
 * (une fraîcheur construite avant le 27/10 ne porte que lui).
 */
export function estInterrompue(
    fraicheur: Fraicheur | null | undefined
): boolean {
    return (
        fraicheur?.collecteInterrompue === true ||
        fraicheur?.droitPerdu === true
    )
}

/**
 * LA COULEUR DU ROND, et rien d'autre (écart E7).
 *
 * Trois états, les jetons déjà posés par la bande : vert quand la fraîcheur est
 * tenue, ambre quand elle ne l'est pas, gris quand rien n'est jamais arrivé —
 * une absence totale n'est pas un retard, et la peindre en ambre laisserait
 * croire qu'une mesure est en chemin.
 *
 * Le rond reste DÉCORATIF (`aria-hidden`) : toute l'information est dans la
 * phrase à côté, jamais dans la couleur seule.
 */
export function classeDuPoint(
    fraicheur: Fraicheur
): "a-jour" | "retard" | "attente" {
    // UN DROIT TOMBÉ N'EST PAS UN RETARD (lot 6a) : l'ambre annoncerait une
    // mesure en chemin, et il n'en vient plus aucune tant que le droit ne
    // revient pas. Le rond reste neutre, la phrase dit le reste. Même chose
    // pour toute collecte interrompue (27/10) : rien n'est en chemin.
    if (estInterrompue(fraicheur)) return "attente"
    // Un verdict que le statut contredit ne peint ni le vert ni l'ambre : on
    // ne sait pas, et accuser au hasard serait pire que se taire.
    if (fraicheur.verdictSu === false) return "attente"
    if (fraicheur.aJour) return "a-jour"
    return fraicheur.aucuneMesure ? "attente" : "retard"
}

/** `2026-09-08` → `08/09/2026`. Rend `null` sur ce qui n'est pas une date. */
export function dateFr(iso: string | null | undefined): string | null {
    if (!iso) return null
    const jour = String(iso).slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(jour)) return null
    const [a, m, j] = jour.split("-")
    return `${j}/${m}/${a}`
}

/** « 1 jour » / « 4 jours ». */
function jours(n: number): string {
    return n <= 1 ? `${n} jour` : `${n} jours`
}

/** Première lettre en capitale, sans toucher au reste. */
function capitale(texte: string): string {
    return texte.charAt(0).toUpperCase() + texte.slice(1)
}

/**
 * La phrase d'un flux en retard. Chaque morceau n'est écrit que s'il est servi :
 * un flux jamais reçu ne dit pas « dernière mesure le null », il dit qu'il n'a
 * jamais rien apporté.
 */
export function ligneDunFlux(flux: WorkflowEnedisStreamEnRetard): string {
    const nom = capitale(nomClientDuStream(flux?.stream))
    const derniere = dateFr(flux?.depuis)
    const morceaux: string[] = [
        derniere
            ? `dernière mesure le ${derniere}`
            : "aucune mesure reçue à ce jour",
    ]
    // ⚠️ `expected_lag_days` NE S'ÉCRIT PLUS (DEV-1845 tranche 2, 17/09). Relevé
    // en prod sur le C2 `653f9d4c3c68b26ee445413d` : ces lignes écrivaient
    // « attendu sous 3 jours » sur les trois flux, courbe comprise. Ce 3 est le
    // SEUIL D'ALERTE de la collecte, le même que le lot 5h a retiré de la voie
    // courte, et pas la promesse — qui est J+1 sur ces trois flux. La promesse
    // ne se lit que dans `status.flux[].attendu_jours` ; tant qu'elle n'est pas
    // servie, aucun attendu ne s'écrit ici.
    if (typeof flux?.lag_days === "number") {
        morceaux.push(`${jours(flux.lag_days)} de retard`)
    }
    return `${nom} : ${morceaux.join(", ")}`
}

/**
 * QUELLE FRAÎCHEUR PEINDRE : le statut ou le verdict (DEV-1845 tranche 2,
 * étendu le 27/10).
 *
 * Le statut prime quand il porte le détail par flux (`status.flux` non vide) ;
 * sinon le verdict, et le statut en repli. ⚠️ Depuis le 27/10 (relecture de
 * #882), le statut prime AUSSI quand il dit la collecte INTERROMPUE (droit
 * tombé ou compteur non téléopérable) : avec `flux: []`, le verdict passait
 * devant et réécrivait « en retard de n j » sur un compteur dont rien n'est en
 * chemin — exactement ce que la ligne unique vient de cesser de dire.
 */
export function fraicheurAPeindre(params: {
    statut: StatusCollectePdl | null | undefined
    verdict: WorkflowEnedisVerdict | null | undefined
    aujourdHui?: string
    contexte?: ContexteDesFlux
}): Fraicheur | null {
    const { statut, verdict, aujourdHui, contexte } = params
    const statutPrime =
        (statut?.flux?.length ?? 0) > 0 || collecteInterrompue(statut)
    const duStatut = statutPrime
        ? fraicheurDuStatut(statut, aujourdHui, contexte)
        : null
    return (
        duStatut ??
        fraicheurDuVerdict(verdict) ??
        fraicheurDuStatut(statut, aujourdHui, contexte)
    )
}

/**
 * La fraîcheur à peindre, ou `null` quand le verdict ne permet pas de la dire.
 *
 * `null` n'est pas un état neutre à afficher : c'est l'ordre de ne RIEN écrire.
 * Écrire « À jour » sans l'avoir lu serait un mensonge servi au client.
 */
export function fraicheurDuVerdict(
    verdict: WorkflowEnedisVerdict | null | undefined
): Fraicheur | null {
    const streams = verdict?.streams_en_retard
    if (!Array.isArray(streams)) return null

    if (streams.length === 0) {
        const derniere = dateFr(verdict?.last_data_date)
        if (!derniere) return null
        return {
            aJour: true,
            derniereMesure: verdict?.last_data_date ?? null,
            lignes: [
                {
                    cle: "a-jour",
                    texte: `À jour, dernière mesure le ${derniere}`,
                },
            ],
        }
    }

    // Le retard du COMPTEUR est celui du flux le plus en retard : c'est lui qui
    // fait l'état du point. Un flux sans `lag_days` n'en fabrique pas un.
    const retards = streams
        .map((flux) => flux?.lag_days)
        .filter((n): n is number => typeof n === "number" && Number.isFinite(n))

    return {
        aJour: false,
        derniereMesure: verdict?.last_data_date ?? null,
        retardJours: retards.length > 0 ? Math.max(...retards) : null,
        // Établi seulement si AUCUN flux en retard n'a jamais rien apporté et
        // que le verdict ne porte pas de dernière donnée.
        aucuneMesure:
            !verdict?.last_data_date && streams.every((flux) => !flux?.depuis),
        // CHAQUE LIGNE PORTE SON ÉTAT, avec le même vocabulaire de pastilles que
        // les lignes par flux : un flux qui n'a jamais rien apporté est GRIS et
        // non ambre, parce qu'une absence totale n'est pas une mesure en chemin.
        lignes: streams.map((flux, i) => ({
            cle: flux?.stream ?? `flux-${i}`,
            texte: ligneDunFlux(flux),
            etat: (flux?.depuis ? "retard" : "attente") as EtatDeLigne,
        })),
    }
}

// ============================================================================
// LA VOIE COURTE : LA FRAÎCHEUR LUE DU STATUT DU COMPTEUR (lot 5a, écart B2)
// ============================================================================

/**
 * LE STATUT AGRÉGÉ NE CONNAÎT PAS LES FLUX. Ce qu'il sait, il le sait pour le
 * compteur ENTIER : une fraîcheur, une cause, un responsable.
 *
 * POURQUOI CE REPLI EXISTE (défaut vu en prod le 15/09 sur les trois compteurs
 * témoins : `[data-testid="fraicheur"]` n'existait sur aucun). Le bloc ne se
 * peignait que depuis `verdict.streams_en_retard`, et ce verdict n'est chargé
 * que sur les workflows `state=run` : un compteur SAIN n'en a aucun, donc aucune
 * fraîcheur ne s'affichait jamais sur un compteur à jour. Le fait manquant est
 * pourtant déjà dans la page, servi par `/source/informations` avec le reste de
 * l'en-tête — aucun appel de plus n'est nécessaire.
 *
 * CE QU'ON N'ÉCRIT PAS, ET POURQUOI : « Tous les flux dus ». Le statut agrège ;
 * il ne dit pas combien de flux ce compteur doit, ni lesquels sont à jour.
 * Écrire une phrase qui porte sur « les flux » depuis un fait qui les ignore
 * serait affirmer plus que ce qui est lu. Le détail par flux (« Courbe 30 min ·
 * retard 72 j », « 1 autre flux à jour ») attend le verdict permanent (lot 5a2).
 *
 * LE TRI-ÉTAT est tenu comme ailleurs : `a_jour` absent ou non booléen = fait
 * NON INSTRUIT, on n'écrit rien. Un compteur dont on ne sait rien n'est jamais
 * dit « à jour ».
 *
 * Aucun seuil de jours ne vit ici (E4) : `retard_jours` est LU, jamais comparé
 * à une constante du front. Et l'attendu n'est écrit QUE quand il est servi PAR
 * FLUX (`attendu_jours_du_flux`, Q19) : l'agrégat `attendu_jours` est un seuil
 * plat de la collecte, pas l'attendu de ce compteur.
 */
export function fraicheurDuStatut(
    statut: StatusCollectePdl | null | undefined,
    /**
     * Le jour de lecture, injectable pour les récits. Défaut : le JOUR CIVIL
     * DE PARIS (`jourDeLecture`), jamais le jour UTC — qui est la veille entre
     * minuit et 2 h, et faisait passer chaque nuit un verdict périmé pour
     * crédible (lot 6b, résidu de la revue 6a).
     */
    aujourdHui: string = jourDeLecture(),
    /**
     * CE QU'IL FAUT POUR NOMMER LES FLUX (DEV-1845 tranche 2) : le segment, le
     * pas de la courbe, les sens servis. Omis = les flux ne sont pas nommés, et
     * le bloc garde la seule ligne de la voie courte.
     */
    contexte?: ContexteDesFlux
): Fraicheur | null {
    if (typeof statut?.a_jour !== "boolean") return null

    // LA COLLECTE EST INTERROMPUE (droit tombé, lot 6a volet 1 ; ou compteur
    // non téléopérable, 27/10) : la page le DIT, et elle ne dit rien d'autre.
    const interrompue = fraicheurDeLaCollecteInterrompue(statut)
    if (interrompue) return interrompue

    const derniere = dateFr(statut.derniere_mesure)
    const su = verdictCredible(statut, aujourdHui)
    const morceaux: string[] = []

    // LE MOT DU VERDICT NE S'ÉCRIT QUE S'IL PEUT ENCORE ÊTRE VRAI. Un statut
    // figé au 22/08 qui se dit « à jour » le 17/09 se contredit : on garde sa
    // date, on laisse tomber son mot.
    if (!su) {
        // rien : la phrase commencera par la date.
    } else if (statut.a_jour) {
        morceaux.push("À jour")
    } else if (typeof statut.retard_jours === "number") {
        morceaux.push(`En retard de ${statut.retard_jours} j`)
    } else {
        morceaux.push("En retard")
    }

    morceaux.push(
        derniere
            ? `dernière mesure le ${derniere}`
            : "aucune mesure reçue à ce jour"
    )

    // L'ATTENDU NE S'ÉCRIT QUE S'IL EST SERVI PAR FLUX (Q19, 17/09) :
    // `attendu_jours` est l'agrégat, plat à 3 sur tout le parc mesuré.
    if (typeof statut.attendu_jours_du_flux === "number") {
        morceaux.push(`attendu J+${statut.attendu_jours_du_flux}`)
    }

    // La cause ne s'écrit que quand elle AJOUTE quelque chose : sur un compteur
    // à jour, elle ne fait que répéter le premier mot.
    if (!statut.a_jour) {
        const cause = causeDuStatutEnClair(statut.cause)
        if (cause) morceaux.push(cause)
    }

    // La phrase commence par une majuscule : sans le mot du verdict, c'est la
    // date qui ouvre, et « dernière mesure le 22/08/2026 » devient « Dernière
    // mesure le 22/08/2026 ».
    if (!su && morceaux.length > 0) morceaux[0] = capitale(morceaux[0])

    // LE DÉTAIL PAR FLUX, quand dapi le sert (DEV-1845 tranche 2). La ligne de
    // tête devient alors le RÉSUMÉ de ce détail (« 3 flux dus · 2 à jour · 1 en
    // retard de 9 j ») : répéter dessous ce que les lignes disent déjà, flux par
    // flux, ferait deux phrases pour un seul fait. Sans `flux`, rien ne change.
    const parFlux = fraicheurParFlux(statut, contexte ?? {}, aujourdHui)
    const lignes: LigneFraicheur[] = parFlux
        ? [
              {
                  cle: "statut",
                  texte: parFlux.resume,
                  // LA PASTILLE DU RÉSUMÉ EST CELLE DES FLUX, pas celle de
                  // l'agrégat : les deux peuvent se contredire, et c'est le
                  // détail par flux que cette ligne résume.
                  etat: (parFlux.tousAJour === true
                      ? "a-jour"
                      : parFlux.tousAJour === false
                        ? "retard"
                        : "attente") as EtatDeLigne,
              },
              ...parFlux.lignes,
          ]
        : [{ cle: "statut", texte: morceaux.join(" · ") }]

    return {
        aJour: statut.a_jour,
        ...(parFlux ? { tousAJour: parFlux.tousAJour } : {}),
        verdictSu: su,
        etat: etatDeLActeur(statut.a_qui_dagir),
        derniereMesure: statut.derniere_mesure ?? null,
        attenduJours:
            typeof statut.attendu_jours_du_flux === "number"
                ? statut.attendu_jours_du_flux
                : null,
        retardJours:
            typeof statut.retard_jours === "number"
                ? statut.retard_jours
                : null,
        // `derniere_mesure: null` est documenté « aucune mesure n'est jamais
        // arrivée » (`StatusCollectePdl`) : là, et là seulement, c'est établi.
        aucuneMesure: statut.derniere_mesure === null,
        lignes,
    }
}

/**
 * LA FRAÎCHEUR D'UN COMPTEUR DONT LE DROIT DE COLLECTE EST TOMBÉ (lot 6a).
 *
 * LE DÉFAUT CORRIGÉ, mesuré en prod le 21/09 sur le C2
 * `653f9d4c3c68b26ee445413d`, dont le consentement est révoqué. dapi servait
 * `{a_jour: false, cause: "consentement_absent", a_qui_dagir: "client",
 * derniere_mesure: "2025-10-09", retard_jours: 347}` et TROIS flux « dus » en
 * retard de 347 j. La page écrivait « 3 flux dus · 3 en retard de 347 j » et
 * trois lignes « attendu J+1 · en retard de 347 j ».
 *
 * Or rien n'est dû : nous n'avons plus le droit de demander ces mesures.
 * Compter des flux dus, chiffrer un retard et promettre un J+1 sur un compteur
 * qu'on n'a plus le droit de lire, c'est accuser la collecte d'un manquement
 * qui n'est pas le sien et cacher au client le seul geste qui rouvrirait tout.
 *
 * UNE SEULE LIGNE, et trois morceaux au plus, chacun conditionné à son fait :
 * « Collecte interrompue depuis le 09/10/2025 · consentement à renouveler · à
 * vous d'agir ». Les mots sont ceux de la carte du compteur (DEV-1620) et de la
 * table des causes : aucun vocabulaire neuf.
 *
 * ⚠️ `flux` N'EST PAS LUE ICI, et c'est voulu : le bloc est le même que dapi
 * serve trois flux en retard (ce qu'il fait aujourd'hui), `[]` (ce qu'il servira
 * quand l'écart amont sera corrigé), ou rien du tout.
 *
 * ÉLARGI LE 27/10 (décision de Mathieu) à la collecte interrompue SANS perte de
 * droit : le compteur non téléopérable lit « Collecte interrompue depuis le
 * 07/10/2026 · compteur non téléopérable · chez Enedis ». Même ligne, mêmes
 * morceaux, aucun mot neuf ; seul `droitPerdu` reste faux, parce qu'aucun
 * droit n'est tombé et qu'aucun geste du client n'est attendu.
 */
function fraicheurDeLaCollecteInterrompue(
    statut: StatusCollectePdl
): Fraicheur | null {
    if (!collecteInterrompue(statut)) return null
    const derniere = dateFr(statut.derniere_mesure)
    const morceaux: string[] = [
        derniere
            ? `Collecte interrompue depuis le ${derniere}`
            : "Collecte interrompue",
    ]
    const cause = causeDuStatutEnClair(statut.cause)
    if (cause) morceaux.push(cause)
    const droitPerdu = droitDeCollecteTombe(statut)
    // SANS PERTE DE DROIT, L'ACTEUR `client` NE S'ÉCRIT PAS (relecture du
    // 27/10) : « à vous d'agir » demanderait un geste qui n'existe pas. Seuls
    // « chez Enedis » et « de notre côté » nomment un côté sans rien demander ;
    // `none` ou un acteur inconnu n'écrivent rien, comme avant.
    const acteur =
        droitPerdu || statut.a_qui_dagir !== "client"
            ? acteurEnClair(statut.a_qui_dagir)
            : null
    if (acteur) morceaux.push(acteur)

    return {
        aJour: false,
        droitPerdu,
        collecteInterrompue: true,
        // NI RETARD NI ATTENDU : ils existent dans le statut servi, ils ne
        // veulent rien dire ici, et les recopier les remettrait dans les
        // phrases par la petite porte (titre, ligne courte, lignes d'onglet).
        retardJours: null,
        attenduJours: null,
        derniereMesure: statut.derniere_mesure ?? null,
        aucuneMesure: statut.derniere_mesure === null,
        // Le rond ne désigne pas de responsable de collecte : personne n'a
        // manqué à quoi que ce soit.
        etat: null,
        lignes: [
            {
                cle: "statut",
                texte: morceaux.join(" · "),
                etat: "attente" as EtatDeLigne,
            },
        ],
    }
}

// ============================================================================
// LA LIGNE COURTE, SOUS CHAQUE ONGLET (lot 5d)
// ============================================================================
//
// La maquette pose sous chaque onglet, à côté du rappel de complétude, une ligne
// de FLUX : « 28/06/2026 · en retard de 72 j ». Elle répète en
// trois mots ce que le bloc de l'en-tête dit en une phrase, mais À L'ENDROIT où
// la donnée se regarde — celui qui est descendu jusqu'au graphe n'a plus
// l'en-tête sous les yeux.
//
// ⚠️ ELLE NE DIT QUE CE QUI EST SERVI. Chaque morceau est conditionné à son
// fait : pas de date servie, pas de date écrite ; pas d'attendu servi, pas de
// « J+n » ; pas de retard chiffré, « en retard » tout court. Aucun morceau n'est
// dérivé d'un autre, et aucun seuil de jours ne vit ici (E4).

/**
 * La ligne courte de fraîcheur, ou `null` quand rien de ce qu'elle porte n'est
 * servi. Une ligne vide ne s'écrit pas : elle n'apprendrait rien.
 */
export function ligneCourteDeFraicheur(
    fraicheur: Fraicheur | null | undefined
): string | null {
    if (!fraicheur) return null
    // LE DROIT TOMBÉ SE DIT ICI AUSSI (lot 6a) : celui qui est descendu jusqu'au
    // graphe n'a plus l'en-tête sous les yeux, et « en retard de 347 j » sous un
    // onglet dirait exactement ce que le bloc de tête vient de cesser de dire.
    // Toute collecte interrompue, droit tombé ou non (27/10).
    if (estInterrompue(fraicheur)) {
        const arret = dateFr(fraicheur.derniereMesure)
        return arret
            ? `Collecte interrompue depuis le ${arret}`
            : "Collecte interrompue"
    }
    const morceaux: string[] = []
    const derniere = dateFr(fraicheur.derniereMesure)
    if (derniere) morceaux.push(derniere)
    else if (fraicheur.aucuneMesure)
        morceaux.push("aucune mesure reçue à ce jour")
    if (typeof fraicheur.attenduJours === "number") {
        morceaux.push(`attendu J+${fraicheur.attenduJours}`)
    }
    // LE VERDICT QUE LE STATUT CONTREDIT NE S'ÉCRIT PAS (complément du 17/09) :
    // seule la date reste, et elle suffit à dire où en est ce compteur.
    if (fraicheur.verdictSu === false) {
        return morceaux.length > 0 ? morceaux.join(" · ") : null
    }
    if (fraicheur.aJour) morceaux.push("à jour")
    else if (typeof fraicheur.retardJours === "number") {
        morceaux.push(`en retard de ${fraicheur.retardJours} j`)
    } else morceaux.push("en retard")
    return morceaux.length > 0 ? morceaux.join(" · ") : null
}

/** Un jour ISO avancé de `n` jours, ou `null` sur ce qui n'est pas un jour. */
function jourDecale(jour: string, n: number): string | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(jour)) return null
    const d = new Date(`${jour}T00:00:00Z`)
    if (Number.isNaN(d.getTime())) return null
    d.setUTCDate(d.getUTCDate() + n)
    return d.toISOString().slice(0, 10)
}

/**
 * LE STATUT SE TIENT-IL DEBOUT TOUT SEUL (complément du 17/09).
 *
 * Deux champs du MÊME statut, et le calendrier : si la dernière mesure servie,
 * augmentée de l'attendu servi, tombe avant la veille du jour de lecture, alors
 * le verdict porté par ce statut date d'un passage antérieur de la collecte et
 * ne peut plus être vrai aujourd'hui.
 *
 * On ne conclut RIEN de plus : ni un retard (on ne sait pas de combien), ni une
 * cause. Un fait manquant ne rend pas le verdict incroyable — sans date servie
 * ou sans attendu servi, il n'y a rien à confronter, et le verdict tient.
 */
function verdictCredible(
    statut: StatusCollectePdl,
    aujourdHui: string
): boolean {
    // UN STATUT QUI SE DIT DÉJÀ EN RETARD NE SE CONTREDIT PAS : il admet ce que
    // les dates racontent, et son `retard_jours` est un fait jugé (E4). La
    // contradiction ne concerne que le « à jour ».
    if (statut.a_jour !== true) return true
    const mesure = String(statut.derniere_mesure ?? "").slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(mesure)) return true
    // UN « À JOUR » SANS ATTENDU SERVI N'EST PAS JUGEABLE (annexe du 17/09).
    //
    // LE FAIT, relevé en prod sur le C2 + P1 `679b86d1df35d0c78886fb9d` :
    // l'agrégat sert `{a_jour: true, cause: "a_jour", derniere_mesure:
    // "2026-08-22", retard_jours: 26, attendu_jours: null}` — la collecte
    // (`update.state: "uptodate"`) est figée au 22/08 et son verdict avec elle.
    // La page écrivait encore « Compteur C2 (SAPHIR), à jour » et « À jour ·
    // dernière mesure le 22/08/2026 » le 17/09, vingt-six jours plus tard.
    //
    // Sans attendu servi, il n'y a RIEN à quoi confronter la date : on ne peut
    // ni confirmer « à jour », ni chiffrer un retard. On garde donc la date, et
    // rien d'autre. Un `a_jour: false` avec son `retard_jours` reste écrit tel
    // quel : celui-là est un verdict, pas une affirmation invérifiable.
    if (typeof statut.attendu_jours !== "number") return false
    const limite = jourDecale(mesure, statut.attendu_jours)
    const veille = jourDecale(String(aujourdHui).slice(0, 10), -1)
    if (!limite || !veille) return true
    return limite >= veille
}

/**
 * L'état de la bande d'un responsable servi, ou `null`.
 *
 * `client` et `none` n'ont pas d'état de bande : une souscription à renouveler
 * n'est ni notre collecte ni un défaut de livraison, et l'inventer donnerait au
 * rond une couleur qui accuse quelqu'un au hasard.
 */
function etatDeLActeur(
    acteur: string | null | undefined
): "homeys" | "enedis" | null {
    if (acteur === "homeys") return "homeys"
    if (acteur === "enedis") return "enedis"
    return null
}

// ============================================================================
// LA FRAÎCHEUR DE CHAQUE FAMILLE, LUE DE SES PROPRES BORNES (lot 5d4)
// ============================================================================
//
// LE DÉFAUT MESURÉ le 16/09 en prod sur le C2 `6a8eebfa` : sous l'onglet
// Dépassements, la page écrivait « 15/09/2026 · attendu J+3 · à jour » alors
// que la dernière durée de dépassement servie datait du 16/01/2026, huit mois
// plus tôt. La ligne recopiait `data_retrieval.status`, qui est l'agrégat DU
// COMPTEUR : il ne parle que du flux principal, jamais des familles qui en
// dérivent ni de celles qui suivent le cycle de relève.
//
// LA RÈGLE : chaque famille lit `grains.<grain>.date_to` de SES variables, et
// le VERDICT (« à jour », « en retard de n j ») n'est recopié que
// là où l'agrégat parle vraiment — c'est-à-dire quand la dernière valeur de la
// famille EST la dernière mesure que l'agrégat date. Ailleurs, la ligne ne
// porte que la date et le mot qui dit de quelle nature est cette date.

/** Du plus fin au plus large. `daily` d'abord : c'est le grain de la page. */
const ORDRE_DES_GRAINS: GrainSeries[] = [
    "daily",
    "natif",
    "hourly",
    "weekly",
    "monthly",
    "periode",
]

/** Le grain dont une famille tire sa date, et la date elle-même. */
export type DerniereValeurDeLaFamille = {
    grain: GrainSeries
    /** `date_to` servie, en ISO court. */
    jour: string
}

/** `date_to` d'une borne servie, ramenée au jour ISO. `null` si non servie. */
function jourDeLaBorne(borne: BornesVariableDataV2 | undefined): string | null {
    if (!borne?.date_to) return null
    const jour = String(borne.date_to).slice(0, 10)
    return /^\d{4}-\d{2}-\d{2}$/.test(jour) ? jour : null
}

/**
 * La dernière valeur que cette famille DÉTIENT, au grain de sa carte.
 *
 * Le grain est `periode` pour la facturation (une période close n'est pas un
 * jour), `daily` partout ailleurs quand il est servi, et à défaut le plus fin
 * des grains servis. La date est la plus récente des bornes de ce grain :
 * plusieurs classes d'une même famille ne se relèvent pas toutes le même jour.
 */
export function derniereValeurDeLaFamille(
    onglet: OngletResolu | null | undefined,
    /**
     * LE SENS DONT ON PARLE (lot 5g). Sans lui, la famille prend la PLUS
     * RÉCENTE des bornes des deux sens : sur le C4 témoin, la carte d'injection
     * aurait porté la date du soutirage, plus fraîche de quatre jours, et
     * annoncé « à jour » une famille qui ne l'est pas.
     */
    direction?: DirectionEnedis | null
): DerniereValeurDeLaFamille | null {
    if (!onglet) return null
    const series = direction
        ? onglet.series.filter((s) => s.direction === direction)
        : onglet.series
    const ordre: GrainSeries[] =
        onglet.cle === "facturation"
            ? ["periode", ...ORDRE_DES_GRAINS]
            : ORDRE_DES_GRAINS
    for (const grain of ordre) {
        const jours = series
            .map((serie) => jourDeLaBorne(serie.bornes?.[grain]))
            .filter((jour): jour is string => jour !== null)
        if (jours.length > 0) {
            return { grain, jour: jours.reduce((a, b) => (a > b ? a : b)) }
        }
    }
    return null
}

/**
 * CE QUE LA DATE DE CHAQUE FAMILLE EST, en quelques mots, quand ce n'est pas
 * une mesure du flux principal. Ces mots viennent de cette table et de nulle
 * part ailleurs : ils ne se dérivent d'aucun chiffre.
 */
const MOT_DE_LA_FAMILLE: Record<string, string> = {
    index: "témoin de communication, jamais un verdict",
    facturation: "stock",
    depassements: "calculée sur la courbe",
}

/**
 * CE QUI S'ÉCRIT QUAND RIEN N'EST JAMAIS ARRIVÉ (lot 5g, E7). « Aucune mesure
 * sur cette fenêtre » ferait croire qu'un autre cadrage en montrerait ; ici,
 * la famille n'a pas de borne du tout. Et ce n'est JAMAIS un 0.
 */
export const AUCUNE_MESURE_RECUE = "aucune mesure reçue à ce jour"

/** Ce que la date nomme, famille par famille. */
const DATE_DE_LA_FAMILLE: Record<string, string> = {
    facturation: "dernière période close le",
    depassements: "dernière durée de dépassement le",
}

/** La PMax d'un compteur relevé au mois suit le cycle, jamais un J+n. */
const MOT_DU_CYCLE = "cycle de relève"

/**
 * LA LIGNE COURTE D'UNE FAMILLE. Trois faits au plus, et aucun fabriqué.
 *
 * Le verdict n'est écrit que pour les familles issues de la COURBE (énergie
 * active, puissance) et seulement quand la dernière valeur de la famille EST
 * celle que l'agrégat date : sinon, l'agrégat ne parle pas de cette famille, et
 * recopier son « à jour » serait affirmer ce que personne n'a jugé.
 *
 * TROIS CAS, ET UN SEUL S'ÉCRIT (addendum 3, vérifié en prod le 16/09) :
 *  (a) une valeur finie est chargée → c'est ELLE qui date la famille ;
 *  (b) sinon, la borne déclarée est antérieure au contexte → famille figée, et
 *      la borne dit jusqu'à quand cette famille a servi ;
 *  (c) sinon → rien n'est daté et rien n'est jugé. Une borne posée sur des
 *      documents à valeur nulle n'est pas une mesure.
 */
export function ligneDeFraicheurDeLaFamille(params: {
    onglet: OngletResolu | null | undefined
    fraicheur: Fraicheur | null | undefined
    /**
     * La dernière abscisse PORTANT UNE VALEUR FINIE dans ce qui est chargé.
     * C'est elle, et pas la borne déclarée, qui date une mesure : sur le C4
     * `638f1080`, la borne du 15/09 comptait 576 abscisses toutes nulles.
     */
    derniereChargee?: string | null
    /** Le premier jour du contexte chargé, pour juger d'une famille figée. */
    debutDuContexte?: string | null
    /**
     * VRAI QUAND `derniereChargee` EST LA FIN D'UNE PÉRIODE (lot 5d4b).
     *
     * Une période de facturation est datée par son DÉBUT sur toute cette page,
     * mais la donnée qu'elle porte va jusqu'à sa FIN : « dernière période close
     * le 01/08/2026 » sur une période qui court au 31/08 annonçait un mois de
     * retard qui n'existe pas. Faux = on ne connaît que le début, et la phrase
     * le dit plutôt que de faire passer un début pour une clôture.
     */
    finDeLaPeriodeConnue?: boolean
    /** Le sens dont cette ligne parle, quand la carte n'en porte qu'un (5g). */
    direction?: DirectionEnedis | null
    /**
     * LE STATUT AGRÉGÉ, pour y lire le flux DE CETTE FAMILLE (DEV-1845 tranche
     * 2). Absent, ou `flux` non servie, ou famille sans flux dû : la ligne garde
     * exactement les règles d'avant (lots 5h et 5g annexe c).
     */
    statut?: StatusCollectePdl | null
    /** Le jour de lecture, injectable pour les récits. */
    aujourdHui?: string
    /**
     * DE QUOI NOMMER LE FLUX QUI DATE CETTE FAMILLE (S1 du 23/09). Omis, la
     * ligne est exactement celle d'avant : un flux qu'on ne sait pas nommer ne
     * reçoit pas de nom inventé.
     */
    contexte?: ContexteDesFlux
}): string | null {
    // Sans onglet, il n'y a pas de famille : rien ne s'écrit.
    if (!params.onglet) return null
    const cle = params.onglet.cle
    const chargee = dateFr(params.derniereChargee)

    // (a) UNE VALEUR FINIE EST CHARGÉE : c'est elle qui date la famille.
    if (chargee) {
        // Une période dont on ne connaît que le début ne se dit pas « close ».
        if (cle === "facturation" && params.finDeLaPeriodeConnue === false) {
            return `dernière période commencée le ${chargee} · ${MOT_DE_LA_FAMILLE.facturation}`
        }
        return phraseDeLaFamille(cle, chargee, params, true)
    }

    // (b) LA BORNE DÉCLARÉE EST ANTÉRIEURE AU CONTEXTE : famille figée. Aucun
    // point n'est chargé, mais on sait jusqu'à quand cette famille a servi.
    const declaree = derniereValeurDeLaFamille(params.onglet, params.direction)
    const debut = params.debutDuContexte
    if (declaree && debut && declaree.jour < debut) {
        const date = dateFr(declaree.jour)
        if (date) {
            return cle === "puissance" && declaree.grain === "monthly"
                ? `${DATE_DE_LA_FAMILLE[cle] ?? "dernière valeur le"} ${date} · ${MOT_DU_CYCLE}`
                : phraseDeLaFamille(cle, date, params, false)
        }
    }

    // (c') AUCUNE BORNE DU TOUT POUR CE SENS (lot 5g, E7) : ce n'est pas la
    // fenêtre qui est en cause, c'est que rien n'est JAMAIS arrivé. Sur la PMax
    // d'injection du C4 témoin, « aucune mesure sur cette fenêtre » laissait
    // croire qu'un autre cadrage en montrerait.
    if (!declaree) return AUCUNE_MESURE_RECUE

    // (c) LA BORNE TOMBE DANS LE CONTEXTE, mais rien de fini n'est servi : on
    // ne date rien et on ne juge rien. Une borne posée sur des documents nuls
    // n'est pas une mesure, et l'écrire en ferait une.
    return "aucune mesure sur cette fenêtre"
}

/** La phrase d'une famille, une fois sa date établie. */
function phraseDeLaFamille(
    cle: string,
    date: string,
    params: {
        fraicheur?: Fraicheur | null
        derniereChargee?: string | null
        direction?: DirectionEnedis | null
        statut?: StatusCollectePdl | null
        aujourdHui?: string
        contexte?: ContexteDesFlux
    },
    verdictPossible: boolean
): string {
    const amorce = DATE_DE_LA_FAMILLE[cle] ?? "dernière valeur le"
    const morceaux = [`${amorce} ${date}`]
    const mot = MOT_DE_LA_FAMILLE[cle]
    if (mot) {
        morceaux.push(mot)
        return morceaux.join(" · ")
    }

    // LA COLLECTE EST INTERROMPUE (droit tombé, lot 6a ; compteur non
    // téléopérable, 27/10) : ni attendu, ni retard, sous aucun onglet. La date
    // reste, elle est un fait.
    if (
        collecteInterrompue(params.statut) ||
        estInterrompue(params.fraicheur)
    ) {
        morceaux.push("collecte interrompue")
        return morceaux.join(" · ")
    }

    // LE FLUX DE CETTE FAMILLE PRIME (DEV-1845 tranche 2) : il porte l'attendu
    // NOMINAL et le verdict jugés sur ce flux-là, pas sur l'agrégat du compteur.
    // La date reste celle qui a été chargée : c'est la seule mesure VUE.
    const flux = fluxDeLaFamille(
        params.statut,
        cle as CleOnglet,
        params.direction
    )
    if (flux) {
        return (
            ligneDeLaFamilleParFlux({
                flux,
                statut: params.statut,
                amorce,
                dateChargee: date,
                aujourdHui: params.aujourdHui,
                contexte: params.contexte,
                libelleDuRappel: LIBELLES_RAPPEL_ONGLET[cle as CleOnglet],
            }) ?? morceaux.join(" · ")
        )
    }
    const fraicheur = params.fraicheur
    // LE VERDICT SE COMPARE À LA DERNIÈRE VALEUR CHARGÉE, jamais à une borne :
    // l'agrégat ne parle de cette famille que s'il date la même mesure.
    const parle =
        verdictPossible &&
        !!fraicheur &&
        fraicheur.verdictSu !== false &&
        String(fraicheur.derniereMesure ?? "").slice(0, 10) ===
            params.derniereChargee
    if (!parle) return morceaux.join(" · ")
    if (typeof fraicheur.attenduJours === "number") {
        morceaux.push(`attendu J+${fraicheur.attenduJours}`)
    }
    if (fraicheur.aJour) morceaux.push("à jour")
    else if (typeof fraicheur.retardJours === "number") {
        morceaux.push(`en retard de ${fraicheur.retardJours} j`)
    } else morceaux.push("en retard")
    return morceaux.join(" · ")
}
